import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IFreightPriceRangeEntityAttributes extends IModelAttributes {
	maxValue: number;
	isFixedPrice: boolean;

	freightCategoryId?: string;
	freightCategory?: Models.FreightCategoryEntity | Models.IFreightCategoryEntityAttributes;
	freightHandlingId?: string;
	freightHandling?: Models.FreightHandlingEntity | Models.IFreightHandlingEntityAttributes;
	rates: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
}
@entity('FreightPriceRangeEntity', 'Freight Price Range')
export default class FreightPriceRangeEntity extends Model implements IFreightPriceRangeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The maximum value for this price range. This could be something like freight tonnes.
	 */
	@observable
	@attribute<FreightPriceRangeEntity, number>()
	public maxValue: number;

	/**
	 * Whether or not the price is a fixed price (which means it does not charge per kg).
	 */
	@observable
	@attribute<FreightPriceRangeEntity, boolean>()
	public isFixedPrice: boolean;

	@observable
	@attribute()
	public freightCategoryId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightCategory: Models.FreightCategoryEntity;

	@observable
	@attribute()
	public freightHandlingId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightHandling: Models.FreightHandlingEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public rates: Models.PriceEntity[] = [];

	constructor(attributes?: Partial<IFreightPriceRangeEntityAttributes>) {
		super(attributes);

		this.rates = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFreightPriceRangeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.maxValue !== undefined) {
				this.maxValue = attributes.maxValue;
			}
			if (attributes.isFixedPrice !== undefined) {
				this.isFixedPrice = attributes.isFixedPrice;
			}
			if (attributes.freightCategoryId !== undefined) {
				this.freightCategoryId = attributes.freightCategoryId;
			}
			if (attributes.freightCategory !== undefined) {
				if (attributes.freightCategory === null) {
					this.freightCategory = attributes.freightCategory;
				} else if (attributes.freightCategory instanceof Models.FreightCategoryEntity) {
					this.freightCategory = attributes.freightCategory;
					this.freightCategoryId = attributes.freightCategory.id;
				} else {
					this.freightCategory = new Models.FreightCategoryEntity(attributes.freightCategory);
					this.freightCategoryId = this.freightCategory.id;
				}
			}
			if (attributes.freightHandlingId !== undefined) {
				this.freightHandlingId = attributes.freightHandlingId;
			}
			if (attributes.freightHandling !== undefined) {
				if (attributes.freightHandling === null) {
					this.freightHandling = attributes.freightHandling;
				} else if (attributes.freightHandling instanceof Models.FreightHandlingEntity) {
					this.freightHandling = attributes.freightHandling;
					this.freightHandlingId = attributes.freightHandling.id;
				} else {
					this.freightHandling = new Models.FreightHandlingEntity(attributes.freightHandling);
					this.freightHandlingId = this.freightHandling.id;
				}
			}
			if (attributes.rates !== undefined && Array.isArray(attributes.rates)) {
				if (!this.rates) {
					this.rates = [];
				}
				for (const model of attributes.rates) {
					if (model instanceof Models.PriceEntity) {
						this.rates.push(model);
					} else {
						this.rates.push(new Models.PriceEntity(model));
					}
				}
			}
		}
	}
}
